import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateQuickMessageRequest,
  QuickMessageQuery,
  UpdateQuickMessageRequest,
} from '@troia-v3/types';

import { quickMessagesService } from '../services/quickMessagesService';

const QUICK_MESSAGES_KEY = ['quick-messages'] as const;

/**
 * List quick messages visible to the current user (company + own personal).
 * `enabled` lets callers (modais/drawers) condition the fetch to open state.
 */
export const useQuickMessages = (query?: QuickMessageQuery, enabled = true) => {
  return useQuery({
    queryKey: [...QUICK_MESSAGES_KEY, query ?? {}],
    queryFn: () => quickMessagesService.getAll(query),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    enabled,
  });
};

export const useCreateQuickMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateQuickMessageRequest) => quickMessagesService.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUICK_MESSAGES_KEY });
    },
  });
};

export const useUpdateQuickMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateQuickMessageRequest }) =>
      quickMessagesService.update(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUICK_MESSAGES_KEY });
    },
  });
};

export const useDeleteQuickMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => quickMessagesService.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUICK_MESSAGES_KEY });
    },
  });
};
